import * as hsluv from 'hsluv';
import { padStart } from 'lodash-es';
import { hex as hexColorContrast } from 'wcag-contrast';

export function isContrastReadable(
  color: string = '#fff',
  backgroundColor: string = '#000',
  ratio: number = 2.6
): boolean {
  return hexColorContrast(backgroundColor, color) > ratio;
}

// Liften from https://css-tricks.com/snippets/javascript/lighten-darken-color/
export function lightenDarkenColor(col: string, amt: number) {
  let color = col;
  let usePound = false;

  if (color[0] === '#') {
    color = col.slice(1);
    usePound = true;
  }

  const num = parseInt(color, 16);

  let r = (num >> 16) + amt;

  if (r > 255) {
    r = 255;
  } else if (r < 0) {
    r = 0;
  }

  let b = ((num >> 8) & 0x00ff) + amt;

  if (b > 255) {
    b = 255;
  } else if (b < 0) {
    b = 0;
  }

  let g = (num & 0x0000ff) + amt;

  if (g > 255) {
    g = 255;
  } else if (g < 0) {
    g = 0;
  }

  return (usePound ? '#' : '') + (g | (b << 8) | (r << 16)).toString(16);
}

/**
 * Ensures hex colors have a # prefix. Does not check if the color itself is valid.
 * @example
 * `ff1234` → `#ff1234`
 */
export const prefixHexColor = <T extends string | null | undefined>(color: T) => {
  if (!color) return color;

  return color.startsWith('#') ? color : `#${color}`;
};

export const stripHexPrefix = (color: string): string => {
  if (!color) return color;

  let colorWithoutHash = color;
  if (color.startsWith('#')) {
    colorWithoutHash = color.substring(1);
  }
  return colorWithoutHash;
};

export const expandShorthandHexColor = (color: string): string => {
  color = stripHexPrefix(color);
  if (color.length === 3) {
    return color
      .split('')
      .map(c => c + c)
      .join('');
  }
  return color;
};

export const mixColors = (colorA: string, colorB: string, weight: number = 50) => {
  const toHex = (d: number): string => d.toString(16);
  const fromHex = (h: string): number => parseInt(h, 16);

  colorA = colorA.startsWith('#') ? colorA.slice(1) : colorA;
  colorB = colorB.startsWith('#') ? colorB.slice(1) : colorB;

  let color = '';

  for (let i = 0; i <= 5; i += 2) {
    // loop through each of the 3 hex components - red, green, and blue
    const componentFromA = fromHex(colorA.substr(i, 2));
    const componentFromB = fromHex(colorB.substr(i, 2));

    // combine the current pairs from each source color, according to the specified weight
    const mixedComponent = toHex(
      Math.floor(componentFromA + (componentFromB - componentFromA) * (weight / 100.0))
    );

    color += padStart(mixedComponent, 2, '0'); // concatenate val to our new color string
  }

  return color;
};

type HSLuv = [number, number, number];
type RGB = [number, number, number];
type HexCode = string;

export function hexToRgb(hexCode: HexCode): RGB {
  if (hexCode.startsWith('#')) {
    hexCode = hexCode.substring(1);
  }
  const bigint = parseInt(hexCode, 16);
  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;

  return [r, g, b];
}

function rgbToHex(rgb: RGB): HexCode {
  return '#' + rgb.map(c => (c & 255).toString(16).padStart(2, '0')).join('');
}

function hsluvToRgb(value: HSLuv): RGB {
  const [r, g, b] = hsluv.hsluvToRgb(value);
  return [r * 255, g * 255, b * 255];
}

function rgbToHsluv(value: RGB): HSLuv {
  const [r, g, b] = value;
  return hsluv.rgbToHsluv([r / 255, g / 255, b / 255]);
}

export function hexAdjustHsluv(hexCode: HexCode, fn: (hsl: HSLuv) => HSLuv): HexCode {
  return rgbToHex(hsluvToRgb(fn(rgbToHsluv(hexToRgb(hexCode)))));
}

interface CreateColorFromContrastOptions<TColor> {
  contrastRatio: number;
  againstBackground?: TColor;
}
export const createColorFromContrast = {
  hex: (hex: HexCode, options: CreateColorFromContrastOptions<HexCode>): HexCode => {
    return rgbToHex(
      createColorFromContrastRgb(hexToRgb(hex), {
        ...options,
        againstBackground:
          options.againstBackground != null ? hexToRgb(options.againstBackground) : undefined,
      })
    );
  },
  rgb: createColorFromContrastRgb,
};

function createColorFromContrastRgb(
  colorRgb: RGB,
  { contrastRatio, againstBackground = [255, 255, 255] }: CreateColorFromContrastOptions<RGB>
): RGB {
  const [h, s, _] = rgbToHsluv(colorRgb);
  const [_h, _s, backgroundLuminance] = rgbToHsluv(againstBackground);

  // Each 1 HSLuv luminance corresponds to 0.1 point of WCAG contrast ratio (approximately)
  const deltaLuminance = contrastRatio * 10;

  const bright = backgroundLuminance + deltaLuminance;
  const dark = backgroundLuminance - deltaLuminance;

  const newLuminance = bright <= 100 ? bright : dark;

  return hsluvToRgb([h, s, newLuminance]);
}

export const contrastRatio = {
  hex: (color1: HexCode, color2: HexCode): number => {
    return constrastRatioRgb(hexToRgb(color1), hexToRgb(color2));
  },
  rgb: constrastRatioRgb,
};

function constrastRatioRgb(color1: RGB, color2: RGB): number {
  // Each 1 HSLuv luminance corresponds to 0.1 point of WCAG contrast ratio (approximately)
  return Math.abs(rgbToHsluv(color1)[2] - rgbToHsluv(color2)[2]) / 10;
}
